import { classNames } from "@/utility/jsx";
import type React from "react";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./DropdownAtom.scss";

const LIST_OFFSET_PX = 4;
const VIEWPORT_PADDING_PX = 8;
const MIN_LIST_MAX_HEIGHT_PX = 80;

export type DropdownAtomOption<T extends string = string> = {
  value: T;
  label: string;
  disabled?: boolean;
};

export type DropdownAtomProps<T extends string = string> = {
  options: readonly DropdownAtomOption<T>[];
  value?: T;
  onChange: (value: T, option: DropdownAtomOption<T>) => void;
  placeholder?: string;
  disabled?: boolean;
  hasError?: boolean;
  className?: string;
  listClassName?: string;
  "data-testid"?: string;
};

export function DropdownAtom<T extends string = string>({
  options,
  value,
  onChange,
  placeholder = "Select...",
  disabled = false,
  hasError = false,
  className,
  listClassName,
  "data-testid": dataTestId,
}: DropdownAtomProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [listStyle, setListStyle] = useState<React.CSSProperties>({});
  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const listboxId = useId();

  const selectedOption = useMemo(() => options.find((option) => option.value === value), [options, value]);

  const computeListStyle = useCallback(() => {
    if (!triggerRef.current) {
      return null;
    }

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const listHeight = listRef.current?.scrollHeight ?? 0;
    const spaceBelow = Math.max(0, window.innerHeight - triggerRect.bottom - LIST_OFFSET_PX - VIEWPORT_PADDING_PX);
    const spaceAbove = Math.max(0, triggerRect.top - LIST_OFFSET_PX - VIEWPORT_PADDING_PX);
    const maxWidth = Math.max(
      triggerRect.width,
      Math.floor(window.innerWidth - triggerRect.left - VIEWPORT_PADDING_PX),
    );
    const shouldOpenAbove = listHeight > spaceBelow && spaceAbove > spaceBelow;
    const availableHeight = shouldOpenAbove ? spaceAbove : spaceBelow;

    return {
      position: "fixed",
      left: `${triggerRect.left}px`,
      right: "auto",
      top: shouldOpenAbove ? "auto" : `${triggerRect.bottom + LIST_OFFSET_PX}px`,
      bottom: shouldOpenAbove ? `${window.innerHeight - triggerRect.top + LIST_OFFSET_PX}px` : "auto",
      minWidth: `${triggerRect.width}px`,
      width: "max-content",
      maxWidth: `${maxWidth}px`,
      maxHeight: `${Math.max(MIN_LIST_MAX_HEIGHT_PX, Math.floor(availableHeight))}px`,
      overflowY: "auto",
      zIndex: 10000,
    } satisfies React.CSSProperties;
  }, []);

  const updateListPosition = useCallback(() => {
    const nextListStyle = computeListStyle();
    if (!nextListStyle) {
      return;
    }
    setListStyle(nextListStyle);
  }, [computeListStyle]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node | null;
      const clickedInsideContainer = !!target && !!containerRef.current?.contains(target);
      const clickedInsideList = !!target && !!listRef.current?.contains(target);
      if (!clickedInsideContainer && !clickedInsideList) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const selectedOptionElement = listRef.current?.querySelector<HTMLButtonElement>(
      '[role="option"][aria-selected="true"]:not(:disabled)',
    );
    const firstEnabledOptionElement = listRef.current?.querySelector<HTMLButtonElement>(
      '[role="option"]:not(:disabled)',
    );

    (selectedOptionElement ?? firstEnabledOptionElement)?.focus();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    updateListPosition();
    window.addEventListener("resize", updateListPosition);
    window.addEventListener("scroll", updateListPosition, true);
    return () => {
      window.removeEventListener("resize", updateListPosition);
      window.removeEventListener("scroll", updateListPosition, true);
    };
  }, [isOpen, updateListPosition]);

  function toggleOpen() {
    if (disabled) {
      return;
    }
    if (!isOpen) {
      updateListPosition();
    }
    setIsOpen((currentState) => !currentState);
  }

  function handleSelect(option: DropdownAtomOption<T>) {
    if (option.disabled) {
      return;
    }
    onChange(option.value, option);
    setIsOpen(false);
  }

  function focusOptionByIndex(index: number) {
    getEnabledOptionElements()[index]?.focus();
  }

  function getEnabledOptionElements() {
    return Array.from(listRef.current?.querySelectorAll<HTMLButtonElement>('[role="option"]:not(:disabled)') ?? []);
  }

  function handleTypeaheadKey(event: React.KeyboardEvent<HTMLButtonElement>, enabledOptions: HTMLButtonElement[]) {
    if (event.key.length !== 1 || event.altKey || event.ctrlKey || event.metaKey) {
      return false;
    }

    const searchCharacter = event.key.trim().toLocaleLowerCase();
    if (!searchCharacter) {
      return false;
    }

    const currentIndex = enabledOptions.findIndex((optionElement) => optionElement === document.activeElement);
    const nextOptions =
      currentIndex < 0
        ? enabledOptions
        : [...enabledOptions.slice(currentIndex + 1), ...enabledOptions.slice(0, currentIndex + 1)];

    const nextMatch = nextOptions.find((optionElement) =>
      optionElement.dataset.optionLabel?.toLocaleLowerCase().startsWith(searchCharacter),
    );
    if (!nextMatch) {
      return false;
    }

    event.preventDefault();
    nextMatch.focus();
    return true;
  }

  function handleOptionKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
    const enabledOptions = getEnabledOptionElements();
    if (enabledOptions.length === 0) {
      return;
    }

    if (handleTypeaheadKey(event, enabledOptions)) {
      return;
    }

    switch (event.key) {
      case "ArrowDown": {
        event.preventDefault();
        const currentIndex = enabledOptions.findIndex((optionElement) => optionElement === document.activeElement);
        const nextIndex = currentIndex < 0 || currentIndex === enabledOptions.length - 1 ? 0 : currentIndex + 1;
        focusOptionByIndex(nextIndex);
        return;
      }
      case "ArrowUp": {
        event.preventDefault();
        const currentIndex = enabledOptions.findIndex((optionElement) => optionElement === document.activeElement);
        const previousIndex = currentIndex <= 0 ? enabledOptions.length - 1 : currentIndex - 1;
        focusOptionByIndex(previousIndex);
        return;
      }
      case "Home": {
        event.preventDefault();
        focusOptionByIndex(0);
        return;
      }
      case "End": {
        event.preventDefault();
        focusOptionByIndex(enabledOptions.length - 1);
        return;
      }
      case "Escape": {
        event.preventDefault();
        setIsOpen(false);
        triggerRef.current?.focus();
        return;
      }
      default:
        return;
    }
  }

  return (
    <div
      ref={containerRef}
      className={classNames(
        "dropdown-atom",
        disabled && "dropdown-atom--disabled",
        hasError && "dropdown-atom--error",
        className,
      )}
      data-testid={dataTestId}
    >
      <button
        ref={triggerRef}
        type="button"
        className="dropdown-atom__trigger"
        onClick={toggleOpen}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
      >
        <span className={classNames("dropdown-atom__label", !selectedOption && "dropdown-atom__label--placeholder")}>
          {selectedOption?.label || placeholder}
        </span>
        <span
          className={classNames("material-icons", "dropdown-atom__chevron", isOpen && "dropdown-atom__chevron--open")}
        >
          expand_more
        </span>
      </button>

      {isOpen &&
        createPortal(
          <div
            id={listboxId}
            role="listbox"
            ref={listRef}
            className={classNames("dropdown-atom__list", listClassName)}
            style={listStyle}
          >
            {options.map((option) => {
              const selected = value === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  data-option-label={option.label}
                  className={classNames(
                    "dropdown-atom__option",
                    selected && "dropdown-atom__option--selected",
                    option.disabled && "dropdown-atom__option--disabled",
                  )}
                  disabled={option.disabled}
                  onClick={() => handleSelect(option)}
                  onKeyDown={handleOptionKeyDown}
                >
                  <span
                    className={classNames(
                      "dropdown-atom__option-check",
                      "material-icons",
                      selected && "dropdown-atom__option-check--visible",
                    )}
                    aria-hidden="true"
                  >
                    check
                  </span>
                  {option.label}
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
}
